import { useState, useEffect } from "react";
import { Plus, Trash2, Edit2, Mail, Clock, Calendar } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { toast } from "sonner";

interface Counterparty {
  id: string;
  name: string;
  type: string;
  timezone: string;
  email_primary: string | null;
  email_cc: string[] | null;
}

interface DocRequirement {
  id: string;
  counterparty_id: string;
  doc_type: string;
  cadence: string;
  sla_days: number;
  active: boolean;
  counterparties?: { name: string };
}

export default function Requirements() {
  const { orgId, familyId } = useTenant();
  const [counterparties, setCounterparties] = useState<Counterparty[]>([]);
  const [requirements, setRequirements] = useState<DocRequirement[]>([]);
  const [loading, setLoading] = useState(true);
  const [counterpartyDialogOpen, setCounterpartyDialogOpen] = useState(false);
  const [requirementDialogOpen, setRequirementDialogOpen] = useState(false);
  const [editingCounterparty, setEditingCounterparty] = useState<Counterparty | null>(null);
  const [editingRequirement, setEditingRequirement] = useState<DocRequirement | null>(null);

  const [formData, setFormData] = useState({
    name: "",
    type: "gp",
    timezone: "UTC",
    email_primary: "",
    email_cc: "",
  });

  const [reqFormData, setReqFormData] = useState({
    counterparty_id: "",
    doc_type: "quarterly_statement",
    cadence: "quarterly",
    sla_days: 45,
  });

  useEffect(() => {
    fetchData();
  }, [orgId, familyId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch counterparties
      const { data: cpData, error: cpError } = await supabase
        .from("counterparties")
        .select("*")
        .eq("org_id", orgId)
        .order("name");

      if (cpError) throw cpError;
      setCounterparties(cpData || []);

      // Fetch requirements with counterparty names
      const { data: reqData, error: reqError } = await supabase
        .from("doc_requirements")
        .select(`
          *,
          counterparties (name)
        `)
        .eq("org_id", orgId)
        .order("created_at", { ascending: false });

      if (reqError) throw reqError;
      setRequirements(reqData || []);
    } catch (err) {
      toast.error("Failed to fetch data: " + (err instanceof Error ? err.message : "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCounterparty = async () => {
    try {
      const emailCcArray = formData.email_cc
        ? formData.email_cc.split(",").map(e => e.trim()).filter(Boolean)
        : [];

      const payload = {
        org_id: orgId,
        family_id: familyId,
        name: formData.name,
        type: formData.type,
        timezone: formData.timezone,
        email_primary: formData.email_primary || null,
        email_cc: emailCcArray.length > 0 ? emailCcArray : null,
      };

      if (editingCounterparty) {
        const { error } = await supabase
          .from("counterparties")
          .update(payload)
          .eq("id", editingCounterparty.id);
        if (error) throw error;
        toast.success("Counterparty updated");
      } else {
        const { error } = await supabase
          .from("counterparties")
          .insert(payload);
        if (error) throw error;
        toast.success("Counterparty created");
      }

      setCounterpartyDialogOpen(false);
      setEditingCounterparty(null);
      setFormData({ name: "", type: "gp", timezone: "UTC", email_primary: "", email_cc: "" });
      fetchData();
    } catch (err) {
      toast.error("Failed to save: " + (err instanceof Error ? err.message : "Unknown error"));
    }
  };

  const handleSaveRequirement = async () => {
    try {
      const payload = {
        org_id: orgId,
        family_id: familyId,
        ...reqFormData,
      };

      if (editingRequirement) {
        const { error } = await supabase
          .from("doc_requirements")
          .update(payload)
          .eq("id", editingRequirement.id);
        if (error) throw error;
        toast.success("Requirement updated");
      } else {
        const { error } = await supabase
          .from("doc_requirements")
          .insert(payload);
        if (error) throw error;
        toast.success("Requirement created");
      }

      setRequirementDialogOpen(false);
      setEditingRequirement(null);
      setReqFormData({ counterparty_id: "", doc_type: "quarterly_statement", cadence: "quarterly", sla_days: 45 });
      fetchData();
    } catch (err) {
      toast.error("Failed to save: " + (err instanceof Error ? err.message : "Unknown error"));
    }
  };

  const handleDeleteCounterparty = async (id: string) => {
    if (!confirm("Delete this counterparty? Associated requirements will also be deleted.")) return;
    
    try {
      const { error } = await supabase
        .from("counterparties")
        .delete()
        .eq("id", id);
      if (error) throw error;
      toast.success("Counterparty deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete: " + (err instanceof Error ? err.message : "Unknown error"));
    }
  };

  const handleDeleteRequirement = async (id: string) => {
    if (!confirm("Delete this requirement?")) return;
    
    try {
      const { error } = await supabase
        .from("doc_requirements")
        .delete()
        .eq("id", id);
      if (error) throw error;
      toast.success("Requirement deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete: " + (err instanceof Error ? err.message : "Unknown error"));
    }
  };

  const openEditCounterparty = (cp: Counterparty) => {
    setEditingCounterparty(cp);
    setFormData({
      name: cp.name,
      type: cp.type,
      timezone: cp.timezone,
      email_primary: cp.email_primary || "",
      email_cc: cp.email_cc?.join(", ") || "",
    });
    setCounterpartyDialogOpen(true);
  };

  const openEditRequirement = (req: DocRequirement) => {
    setEditingRequirement(req);
    setReqFormData({
      counterparty_id: req.counterparty_id,
      doc_type: req.doc_type,
      cadence: req.cadence,
      sla_days: req.sla_days,
    });
    setRequirementDialogOpen(true);
  };

  const docTypeLabels: Record<string, string> = {
    quarterly_statement: "Quarterly Statement",
    k1: "K-1",
    monthly_report: "Monthly Report",
    capital_call_confirm: "Capital Call Confirmation",
    distribution_confirm: "Distribution Confirmation",
  };

  if (loading) {
    return <div className="flex-1 p-8">Loading...</div>;
  }

  return (
    <div className="flex-1 space-y-6 p-8">
      
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Document Requirements</h1>
        <p className="text-muted-foreground">
          Manage counterparties and expected documents for automated chasing
        </p>
      </div>

      <Tabs defaultValue="counterparties" className="space-y-4">
        <TabsList>
          <TabsTrigger value="counterparties">Counterparties</TabsTrigger>
          <TabsTrigger value="requirements">Requirements</TabsTrigger>
        </TabsList>

        <TabsContent value="counterparties" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Counterparties</CardTitle>
                <CardDescription>GPs, investees, and administrators you chase for documents</CardDescription>
              </div>
              <Dialog open={counterpartyDialogOpen} onOpenChange={setCounterpartyDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={() => {
                    setEditingCounterparty(null);
                    setFormData({ name: "", type: "gp", timezone: "UTC", email_primary: "", email_cc: "" });
                  }}>
                    <Plus className="h-4 w-4" />
                    Add Counterparty
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingCounterparty ? "Edit" : "Add"} Counterparty</DialogTitle>
                    <DialogDescription>
                      Define who you'll chase for documents
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Name</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Fund VII, ABC Corp"
                      />
                    </div>
                    <div>
                      <Label>Type</Label>
                      <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="gp">GP</SelectItem>
                          <SelectItem value="investee">Investee</SelectItem>
                          <SelectItem value="admin">Administrator</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Time Zone</Label>
                      <Input
                        value={formData.timezone}
                        onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                        placeholder="UTC, America/New_York"
                      />
                    </div>
                    <div>
                      <Label>Primary Email</Label>
                      <Input
                        type="email"
                        value={formData.email_primary}
                        onChange={(e) => setFormData({ ...formData, email_primary: e.target.value })}
                        placeholder="ir@fundvii.com"
                      />
                    </div>
                    <div>
                      <Label>CC Emails (comma separated)</Label>
                      <Input
                        value={formData.email_cc}
                        onChange={(e) => setFormData({ ...formData, email_cc: e.target.value })}
                        placeholder="cfo@fundvii.com, admin@fundvii.com"
                      />
                    </div>
                    <Button onClick={handleSaveCounterparty} className="w-full">
                      Save
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Time Zone</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {counterparties.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground">
                        No counterparties yet. Add one to get started.
                      </TableCell>
                    </TableRow>
                  ) : (
                    counterparties.map((cp) => (
                      <TableRow key={cp.id}>
                        <TableCell className="font-medium">{cp.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{cp.type.toUpperCase()}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm">
                            <Mail className="h-3 w-3" />
                            {cp.email_primary || "—"}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm">
                            <Clock className="h-3 w-3" />
                            {cp.timezone}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => openEditCounterparty(cp)}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteCounterparty(cp.id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="requirements" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Document Requirements</CardTitle>
                <CardDescription>Define what documents you expect and when</CardDescription>
              </div>
              <Dialog open={requirementDialogOpen} onOpenChange={setRequirementDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    onClick={() => {
                      setEditingRequirement(null);
                      setReqFormData({ counterparty_id: "", doc_type: "quarterly_statement", cadence: "quarterly", sla_days: 45 });
                    }}
                    disabled={counterparties.length === 0}
                  >
                    <Plus className="h-4 w-4" />
                    Add Requirement
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingRequirement ? "Edit" : "Add"} Requirement</DialogTitle>
                    <DialogDescription>
                      Set expectations for a specific document type
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Counterparty</Label>
                      <Select
                        value={reqFormData.counterparty_id}
                        onValueChange={(v) => setReqFormData({ ...reqFormData, counterparty_id: v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select counterparty" />
                        </SelectTrigger>
                        <SelectContent>
                          {counterparties.map((cp) => (
                            <SelectItem key={cp.id} value={cp.id}>
                              {cp.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Document Type</Label>
                      <Select
                        value={reqFormData.doc_type}
                        onValueChange={(v) => setReqFormData({ ...reqFormData, doc_type: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(docTypeLabels).map(([key, label]) => (
                            <SelectItem key={key} value={key}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Cadence</Label>
                      <Select
                        value={reqFormData.cadence}
                        onValueChange={(v) => setReqFormData({ ...reqFormData, cadence: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="quarterly">Quarterly</SelectItem>
                          <SelectItem value="annual">Annual</SelectItem>
                          <SelectItem value="event">Event-based</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>SLA (days after period end)</Label>
                      <Input
                        type="number"
                        value={reqFormData.sla_days}
                        onChange={(e) => setReqFormData({ ...reqFormData, sla_days: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                    <Button onClick={handleSaveRequirement} className="w-full">
                      Save
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Counterparty</TableHead>
                    <TableHead>Document Type</TableHead>
                    <TableHead>Cadence</TableHead>
                    <TableHead>SLA</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requirements.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground">
                        No requirements configured. Add one to start tracking documents.
                      </TableCell>
                    </TableRow>
                  ) : (
                    requirements.map((req) => (
                      <TableRow key={req.id}>
                        <TableCell className="font-medium">
                          {req.counterparties?.name || "Unknown"}
                        </TableCell>
                        <TableCell>{docTypeLabels[req.doc_type] || req.doc_type}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{req.cadence}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {req.sla_days}d
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={req.active ? "default" : "secondary"}>
                            {req.active ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => openEditRequirement(req)}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteRequirement(req.id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
